import {
  FINANCIAL_PROFILE_NUMERIC_INPUT_IDS,
  FINANCIAL_PROFILE_QUESTION_GLOBAL_ORDER,
  FINANCIAL_PROFILE_QUESTION_PANEL_INDEX,
} from '../OnboardingAgent/FinancialProfilePanel';
import { financialProfileQuestionLabels, financialProfileSectionTitles } from '../../utils/contentCatalog';

function buildQuestionMap(questionsSource) {
  const map = new Map();
  if (!questionsSource?.categories) return map;
  questionsSource.categories.forEach((category) => {
    category.questions?.forEach((q) => {
      if (q?.id) map.set(q.id, { ...q, categoryId: category.id });
    });
  });
  return map;
}

function humanizeKey(key) {
  return String(key).replace(/_/g, ' ');
}

function withFinancialProfilePanelTitle(question) {
  if (!question?.id) return question;
  const label = financialProfileQuestionLabels()[question.id];
  if (!label) return question;
  return { ...question, title: label };
}

function inferSyntheticQuestion(key) {
  const id = String(key);
  if (id === 'children_college_years') {
    return { id, title: humanizeKey(id), type: 'multiple_input_number' };
  }
  if (id === 'state' || id === 'city') {
    return { id, title: humanizeKey(id), type: 'select' };
  }
  if (id === 'your_year_of_birth' || id === 'partner_year_of_birth_of_birth') {
    return { id, title: humanizeKey(id), type: 'year' };
  }
  if (FINANCIAL_PROFILE_NUMERIC_INPUT_IDS.has(id)) {
    return { id, title: humanizeKey(id), type: 'number' };
  }
  if (id === 'pension_plan' || id === 'annuity_plan') {
    return { id, title: humanizeKey(id), type: 'multiple_choice', option: ['Yes', 'No'] };
  }
  return { id, title: humanizeKey(id), type: 'free_text' };
}

/** Order question keys like FinancialProfilePanel (global order), then id string for unknown keys. */
function sortQuestionKeysByFinancialProfilePanelOrder(keys) {
  return [...keys].sort((a, b) => {
    const oa = FINANCIAL_PROFILE_QUESTION_GLOBAL_ORDER[a];
    const ob = FINANCIAL_PROFILE_QUESTION_GLOBAL_ORDER[b];
    const va = oa === undefined ? Number.MAX_SAFE_INTEGER : oa;
    const vb = ob === undefined ? Number.MAX_SAFE_INTEGER : ob;
    if (va !== vb) return va - vb;
    return String(a).localeCompare(String(b));
  });
}

/**
 * Build profile section categories from every key in `financial_profile`.
 * Section names, field order, and labels/synthetic types align with FinancialProfilePanel.
 */
export function buildCategoriesFromFinancialProfile(financialProfile, questionsSource, sectionTitles) {
  const fp = financialProfile && typeof financialProfile === 'object' && !Array.isArray(financialProfile)
    ? financialProfile
    : {};
  const keys = Object.keys(fp);
  const questionMap = buildQuestionMap(questionsSource);
  const templateCats = questionsSource?.categories || [];
  const titles = financialProfileSectionTitles(sectionTitles);
  const indexedCount = Object.values(FINANCIAL_PROFILE_QUESTION_PANEL_INDEX).reduce(
    (max, index) => (Number.isFinite(index) ? Math.max(max, index) : max),
    -1,
  ) + 1;
  const panelCount = Math.max(titles.length, indexedCount);

  const byPanel = new Map();
  const fallbackByDigit = new Map();

  for (const key of keys) {
    if (!key || typeof key !== 'string') continue;
    const panelIx = FINANCIAL_PROFILE_QUESTION_PANEL_INDEX[key];
    if (panelIx !== undefined) {
      if (!byPanel.has(panelIx)) byPanel.set(panelIx, []);
      byPanel.get(panelIx).push(key);
    } else {
      const m = key.match(/^(\d+)_/);
      const digit = m ? m[1] : '0';
      if (!fallbackByDigit.has(digit)) fallbackByDigit.set(digit, []);
      fallbackByDigit.get(digit).push(key);
    }
  }

  const buildOneCategory = (categoryId, sectionName, catKeys) => {
    const orderedKeys = sortQuestionKeysByFinancialProfilePanelOrder(catKeys);
    const questions = orderedKeys.map((key) => {
      const meta = questionMap.get(key);
      const base = meta ?? inferSyntheticQuestion(key);
      return withFinancialProfilePanelTitle(base);
    });

    return {
      id: categoryId,
      name: sectionName,
      questions,
    };
  };

  const out = [];

  for (let p = 0; p < panelCount; p++) {
    const catKeys = byPanel.get(p);
    if (!catKeys?.length) continue;
    out.push(
      buildOneCategory(`fp-panel-${p}`, titles[p] || `Section ${p + 1}`, catKeys)
    );
  }

  const fallbackDigits = [...fallbackByDigit.keys()].sort((a, b) => {
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });

  for (const digit of fallbackDigits) {
    const catKeys = fallbackByDigit.get(digit);
    const template = templateCats.find((c) => String(c.id) === String(digit));
    out.push(
      buildOneCategory(`fp-fallback-${digit}`, template?.name || `Section ${digit}`, catKeys)
    );
  }

  return out;
}
